import React, { useState, useMemo } from 'react';
import CollapsibleSection from './CollapsibleSection';
import DiagnosisFinding from './DiagnosisFinding';
import TestBoxplot from './TestBoxplot';
import './DiagnosticResults.css';

function DiagnosticResults({ results }) {
    const { pathologies, labTests, metadata } = results;

    // Categorize findings by significance
    const categorizedFindings = useMemo(() => {
        const high = pathologies.filter(p => p.total_weight >= 0.5);
        const moderate = pathologies.filter(p => p.total_weight >= 0.1 && p.total_weight < 0.5);
        const low = pathologies.filter(p => p.total_weight >= 0.01 && p.total_weight < 0.1);

        return { high, moderate, low };
    }, [pathologies]);

    // Get all contributing tests and non-contributing tests
    const testAnalysis = useMemo(() => {
        const contributingMarkers = new Set();
        pathologies.forEach(pathology => {
            pathology.contributing_markers?.forEach(marker => {
                contributingMarkers.add(marker.marker);
            });
        });

        const contributing = labTests.filter(test => {
            const testName = test.alias || test.test_name;
            return contributingMarkers.has(testName);
        });

        const nonContributing = labTests.filter(test => {
            const testName = test.alias || test.test_name;
            return !contributingMarkers.has(testName);
        });

        return { contributing, nonContributing };
    }, [pathologies, labTests]);

    const findTestData = (markerName) => {
        return labTests.find(test =>
            (test.alias === markerName) || (test.test_name === markerName)
        );
    };

    return (
        <div className="diagnostic-results">
            <div className="results-header">
                <h2>Diagnostic Analysis Results</h2>
                <div className="lab-statistics">
                    <div className="stat-item">
                        <span className="stat-label">Total Tests:</span>
                        <span className="stat-value">{labTests.length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Contributing Tests:</span>
                        <span className="stat-value">{testAnalysis.contributing.length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Total Diagnoses:</span>
                        <span className="stat-value">{pathologies.length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">High Significance:</span>
                        <span className="stat-value">{categorizedFindings.high.length}</span>
                    </div>
                </div>
            </div>

            {/* High Significance Findings */}
            <CollapsibleSection
                title={`High Significance Findings (≥0.5) - ${categorizedFindings.high.length} findings`}
                defaultOpen={true}
                className="high-significance"
            >
                {categorizedFindings.high.map((finding, index) => (
                    <DiagnosisFinding
                        key={index}
                        finding={finding}
                        findTestData={findTestData}
                        className="high-finding"
                    />
                ))}
            </CollapsibleSection>

            {/* Moderate Significance Findings */}
            <CollapsibleSection
                title={`Moderate Significance Findings (0.1-0.5) - ${categorizedFindings.moderate.length} findings`}
                defaultOpen={false}
                className="moderate-significance"
            >
                {categorizedFindings.moderate.map((finding, index) => (
                    <DiagnosisFinding
                        key={index}
                        finding={finding}
                        findTestData={findTestData}
                        className="moderate-finding"
                    />
                ))}
            </CollapsibleSection>

            {/* Low Significance Findings */}
            <CollapsibleSection
                title={`Low Significance Findings (0.01-0.1) - ${categorizedFindings.low.length} findings`}
                defaultOpen={false}
                className="low-significance"
            >
                {categorizedFindings.low.map((finding, index) => (
                    <DiagnosisFinding
                        key={index}
                        finding={finding}
                        findTestData={findTestData}
                        className="low-finding"
                    />
                ))}
            </CollapsibleSection>

            {/* All Contributing Tests Summary */}
            <CollapsibleSection
                title={`All Contributing Tests Summary - ${testAnalysis.contributing.length} tests`}
                defaultOpen={false}
                className="contributing-tests"
            >
                <div className="test-grid">
                    {testAnalysis.contributing.map((test, index) => (
                        <TestBoxplot
                            key={index}
                            testData={test}
                            showDetails={true}
                            className="summary-boxplot"
                        />
                    ))}
                </div>
            </CollapsibleSection>

            {/* Non-Contributing Tests */}
            <CollapsibleSection
                title={`Non-Contributing Tests - ${testAnalysis.nonContributing.length} tests`}
                defaultOpen={false}
                className="non-contributing-tests"
            >
                <div className="non-contributing-list">
                    <p className="section-description">
                        These tests were not used in diagnostic scoring because they lack marker definitions in the system:
                    </p>
                    <div className="test-list">
                        {testAnalysis.nonContributing.map((test, index) => (
                            <div key={index} className="non-contributing-item">
                                <span className="test-name">{test.alias || test.test_name}</span>
                                <span className="test-value">{test.result} {test.units}</span>
                                <span className="test-ref">Ref: {test.ref || `${test.lab_low}-${test.lab_high}`}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </CollapsibleSection>

            {/* Complete Results Summary */}
            <CollapsibleSection
                title="Complete Diagnostic Results"
                defaultOpen={false}
                className="complete-results"
            >
                <div className="complete-results-table">
                    <table>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Diagnosis</th>
                                <th>Weight</th>
                                <th>Contributing Markers</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pathologies.map((pathology, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{pathology.diagnosis}</td>
                                    <td className="weight-cell">
                                        <span className={`weight-badge ${
                                            pathology.total_weight >= 0.5 ? 'high' :
                                            pathology.total_weight >= 0.1 ? 'moderate' : 'low'
                                        }`}>
                                            {pathology.total_weight.toFixed(4)}
                                        </span>
                                    </td>
                                    <td>
                                        {pathology.contributing_markers?.map(m => m.marker).join(', ') || 'None'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </CollapsibleSection>
        </div>
    );
}

export default DiagnosticResults;